/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import 'core-js/stable';
import 'regenerator-runtime/runtime';

import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Security } from '@okta/okta-react';
import {
  BrowserRouter as Router,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';

import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

import { Provider } from 'react-redux';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import StylesProvider from '@mui/styles/StylesProvider';
import { hotjar } from 'react-hotjar';

import {
  asyncWithLDProvider,
  withLDConsumer,
} from 'launchdarkly-react-client-sdk';

import MaintenancePage from '@app/containers/MaintenancePage';

import * as analytics from '@app/helpers/analytics';
import theme from '@app/theme';

// Import root app
import App from '@app/containers/App/Loadable';
import SnackbarProvider from '@app/containers/SnackbarProvider';
import ScrollToTop from '@app/components/ScrollToTop';
import I18nToggle from '@app/components/I18nToggle';

// Load the favicon - now handled by Vite from public directory
// import './images/favicon.ico'; // No longer needed - favicon is in public/
import { isUnsupportedBrowser } from '@app/helpers/browser';

import store from '@app/configureStore';
import UnsupportedBrowser from '@app/components/UnsupportedBrowser';
import { getOktaConfig } from '@app/okta.config';
import { getEnvConfig } from '@app/env.config';
import { shouldIgnoreError } from '@app/helpers/errorFiltering';

const { HOTJAR_ID, ENVIRONMENT } = getEnvConfig();

const MOUNT_NODE = document.getElementById('app');

const initializeHotjar = () => {
  // 6 is 'Hotjar Snippet Version' or hjsv
  hotjar.initialize({ id: HOTJAR_ID, sv: 6 });
};
initializeHotjar();

const MaintenanceSwitch = withLDConsumer()(props => {
  return props.flags.maintenancePage ? <MaintenancePage /> : <App />;
});

const SecuredRoutes = () => {
  const oktaConfig = getOktaConfig();
  const navigate = useNavigate();

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  const oktaAuth = new OktaAuth(oktaConfig.oidc);

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <SnackbarProvider>
        <MaintenanceSwitch />
      </SnackbarProvider>
    </Security>
  );
};

const root = createRoot(MOUNT_NODE);

const render = async () => {
  const isUnsupported = isUnsupportedBrowser();
  const LDProvider = await asyncWithLDProvider({
    clientSideID: getEnvConfig().LAUNCHDARKLY_CLIENT_ID,
  });

  // Temporarily disable MSW to isolate other issues
  // if (process.env.NODE_ENV === 'development') {
  //   const { worker } = await import('./mocks/browser');
  //   worker.start({
  //     onUnhandledRequest: 'bypass',
  //   });
  // }

  root.render(
    <Provider store={store}>
      <Router>
        <LDProvider>
          <ThemeProvider theme={theme}>
            <StylesProvider>
              <CssBaseline />
              <ScrollToTop />
              {ENVIRONMENT !== 'Production' && <I18nToggle />}
              {isUnsupported ? <UnsupportedBrowser /> : <SecuredRoutes />}
            </StylesProvider>
          </ThemeProvider>
        </LDProvider>
      </Router>
    </Provider>,
  );
};

const sentryDsn = getEnvConfig().SENTRY_DSN;

const browserTracing = new BrowserTracing({
  routingInstrumentation: Sentry.reactRouterV6Instrumentation(
    useEffect,
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes,
  ),
});

Sentry.init({
  dsn: sentryDsn,
  environment: ENVIRONMENT,
  ...(getEnvConfig().VERSION && {
    release: `bennie-web@${getEnvConfig().VERSION}`,
  }),
  integrations: [browserTracing],
  tracesSampleRate: 0.2,
  beforeSendTransaction(event) {
    if (
      !ENVIRONMENT ||
      ENVIRONMENT === 'Local' ||
      event.transaction === '/implicit/callback'
    ) {
      return null;
    }
    return event;
  },
  beforeSend(event, hint) {
    const originalError = hint.originalException;

    if (shouldIgnoreError(originalError)) {
      return null;
    }

    if (!ENVIRONMENT || ENVIRONMENT === 'Local') {
      return null;
    }

    return event;
  },
});

analytics.init(getEnvConfig().MIXPANEL_TOKEN, {
  debug: ENVIRONMENT !== 'Production',
});

render();
